import { type SubmitHandler, useForm } from 'react-hook-form';

import {
    createEdits,
    createReusableContent,
    normalizeReusableContentTitle,
    type RevisionReusableContent,
} from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Submit,
    TextField,
    TextInput,
    useDialogClose,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { useLoggedInUser } from '../../state';

/**
 * Dialog to create a new reusable content.
 */
export function CreateReusableContentDialog(props: {
    onInsert: (reusableContent: RevisionReusableContent) => void;
}) {
    const { onInsert } = props;
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Create reusable content</Heading>
                <Description>
                    Enter a title to represent the reusable content. Your team and you can then use
                    this content in multiple places.
                </Description>
            </DialogHeader>
            <CreateReusableContentForm onInsert={onInsert} />
        </Dialog>
    );
}

type CreateReusableContentFormInputs = {
    title: string;
};

function CreateReusableContentForm(props: {
    onInsert: (reusableContent: RevisionReusableContent) => void;
}) {
    const { onInsert } = props;
    const close = useDialogClose();
    const branch = useHiveBranch();
    const user = useLoggedInUser();

    const form = useForm<CreateReusableContentFormInputs>({
        defaultValues: { title: '' },
    });

    const onSubmit: SubmitHandler<CreateReusableContentFormInputs> = async (data) => {
        assertIsDefined(branch);

        const reusableContent = createReusableContent({
            title: normalizeReusableContentTitle(data.title),
            edits: createEdits([user.uid]),
        });

        branch.change((change) => {
            change.insertReusableContent(reusableContent);
        });

        onInsert(reusableContent);
        close();
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <TextField name="title">
                    <TextInput
                        autoFocus
                        placeholder="Recognizable title for your team"
                        type="text"
                    />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Create" />
            </DialogFooter>
        </Form>
    );
}
